import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, ArrowLeft, Layers, Wrench, Car, Gauge, ShieldCheck, Trophy, Settings, Headphones } from 'lucide-react';
import { IMAGES, FEATURED_BUILDS, SERVICES, WHY_US, PARTNER_BRANDS, PRODUCTS } from '../mock';

const iconMap = { Layers, Wrench, Car, Gauge, ShieldCheck, Trophy, Settings, Headphones };

const FeaturedBuildsCarousel = () => {
  const fallback = useMemo(
    () =>
      FEATURED_BUILDS
        .map((b) => {
          const p = PRODUCTS.find((x) => x.slug === b.slug);
          return { slug: b.slug, name: b.title, label: b.subtitle, price: b.price, image: p?.images?.[0] };
        })
        .filter((b) => b.image),
    []
  );

  const [featured, setFeatured] = useState(null);

  useEffect(() => {
    let alive = true;
    fetch(`${process.env.REACT_APP_BACKEND_URL}/api/featured`)
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => {
        if (!alive || !d) return;
        const items = (d.products || [])
          .filter((p) => p.image)
          .map((p) => ({
            slug: p.slug,
            name: p.name,
            label: (p.collectionName || p.brand || '').toUpperCase(),
            price: p.price,
            image: p.image,
          }));
        setFeatured(items);
      })
      .catch(() => {});
    return () => { alive = false; };
  }, []);

  // Owner picks when at least one is marked; otherwise the curated fallback set.
  const builds = featured && featured.length > 0 ? featured : fallback;

  const PER_PAGE = 3;
  const maxIndex = Math.max(0, builds.length - PER_PAGE);
  const [start, setStart] = useState(0);

  useEffect(() => { setStart((s) => Math.min(s, Math.max(0, builds.length - PER_PAGE))); }, [builds.length]);

  const prev = () => setStart((s) => Math.max(0, s - 1));
  const next = () => setStart((s) => Math.min(maxIndex, s + 1));

  const visible = builds.slice(start, start + PER_PAGE);
  const canPrev = start > 0;
  const canNext = start < maxIndex;
  const totalSlides = maxIndex + 1;
  const currentSlide = start;

  return (
    <section className="py-24 bg-neutral-950">
      <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between mb-10 gap-6">
          <div>
            <div className="font-display tracking-[0.3em] text-xs text-[#FF6B00] font-semibold mb-3">FEATURED PRODUCTS</div>
            <h2 className="font-display text-4xl md:text-5xl lg:text-6xl tracking-tight">PARTS THAT PERFORM.</h2>
            <p className="text-neutral-400 mt-3 max-w-xl">Hand-selected dry carbon components from our most popular collections.</p>
          </div>
          <div className="flex items-center gap-3">
            <button
              type="button"
              onClick={prev}
              disabled={!canPrev}
              aria-label="Previous builds"
              className="h-12 w-12 border-2 flex items-center justify-center transition-colors disabled:opacity-30 disabled:cursor-not-allowed border-white/40 text-white hover:border-[#FF6B00] hover:text-[#FF6B00] enabled:hover:bg-[#FF6B00]/10"
            >
              <ArrowLeft size={20} />
            </button>
            <button
              type="button"
              onClick={next}
              disabled={!canNext}
              aria-label="Next builds"
              className="h-12 w-12 border-2 flex items-center justify-center transition-colors disabled:opacity-30 disabled:cursor-not-allowed border-white/40 text-white hover:border-[#FF6B00] hover:text-[#FF6B00] enabled:hover:bg-[#FF6B00]/10"
            >
              <ArrowRight size={20} />
            </button>
            <Link
              to="/shop"
              className="ml-2 hidden md:inline-block border-2 border-white/70 hover:border-white hover:bg-white/10 transition-colors px-5 py-3 font-display tracking-[0.22em] text-sm font-bold"
            >
              VIEW ALL
            </Link>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {visible.map((b) => (
            <Link
              key={b.slug}
              to={`/product/${b.slug}`}
              className="group relative overflow-hidden block"
            >
              <div className="aspect-[4/3] overflow-hidden">
                <img
                  src={b.image}
                  alt={b.name}
                  loading="lazy"
                  className="w-full h-full object-cover transform group-hover:scale-105 transition-transform duration-700"
                />
              </div>
              <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/30 to-transparent pointer-events-none" />
              <div className="absolute bottom-0 left-0 right-0 p-6 flex items-end justify-between gap-4">
                <div className="min-w-0">
                  {b.label && (
                    <div className="font-display tracking-[0.22em] text-[10px] text-[#FF6B00] font-semibold mb-2 truncate">
                      {b.label}
                    </div>
                  )}
                  <h3 className="font-display tracking-wide text-xl md:text-2xl text-white leading-tight line-clamp-2">
                    {b.name}
                  </h3>
                  {(b.price || b.price === 0) && (
                    <div className="text-neutral-200 text-xs mt-2 font-display tracking-[0.18em]">
                      FROM ${Number(b.price).toLocaleString()} USD
                    </div>
                  )}
                </div>
                <div className="h-10 w-10 border-2 border-white/60 group-hover:border-[#FF6B00] group-hover:bg-[#FF6B00] flex items-center justify-center shrink-0 transition-colors">
                  <ArrowRight className="text-white" size={18} />
                </div>
              </div>
            </Link>
          ))}
        </div>

        {totalSlides > 1 && (
          <div className="flex justify-center gap-2 mt-8">
            {Array.from({ length: totalSlides }).map((_, i) => (
              <button
                key={i}
                type="button"
                onClick={() => setStart(i)}
                aria-label={`Go to slide ${i + 1}`}
                className={`h-1 transition-all ${
                  i === currentSlide ? 'w-10 bg-[#FF6B00]' : 'w-6 bg-white/25 hover:bg-white/50'
                }`}
              />
            ))}
          </div>
        )}
      </div>
    </section>
  );
};

const HERO_SLIDES = [
  {
    image: 'https://images.unsplash.com/photo-1516298252535-cf2ac5147f9b?auto=format&fit=crop&w=2400&q=80',
    label: 'McLaren · Track Spec',
    cta: { label: 'SHOP McLAREN', to: '/collections/mclaren-gt' },
  },
  {
    image: 'https://images.pexels.com/photos/29008113/pexels-photo-29008113.jpeg?auto=compress&cs=tinysrgb&w=2400',
    label: 'Lamborghini · Mid-Engine V10',
    cta: { label: 'SHOP HURACÁN', to: '/collections/lamborghini-huracan' },
  },
  {
    image: 'https://images.pexels.com/photos/34424673/pexels-photo-34424673.jpeg?auto=compress&cs=tinysrgb&w=2400',
    label: 'Mercedes-AMG · Golden Hour',
    cta: { label: 'SHOP AMG GT', to: '/collections/mercedes-amg-gt' },
  },
  {
    image: 'https://images.pexels.com/photos/17377918/pexels-photo-17377918.jpeg?auto=compress&cs=tinysrgb&w=2400',
    label: 'Italian Red · Mid-Engine',
    cta: { label: 'SHOP FERRARI', to: '/collections/ferrari-f8' },
  },
  {
    image: 'https://images.pexels.com/photos/34214702/pexels-photo-34214702.jpeg?auto=compress&cs=tinysrgb&w=2400',
    label: 'BMW M Power · Track Day',
    cta: { label: 'SHOP BMW M3 / M4', to: '/collections/bmw-m3-m4' },
  },
  {
    image: 'https://images.pexels.com/photos/34430452/pexels-photo-34430452.jpeg?auto=compress&cs=tinysrgb&w=2400',
    label: 'Tesla · Highland Carbon',
    cta: { label: 'SHOP MODEL 3', to: '/collections/tesla-model-3-highland' },
  },
];

const HERO_API = process.env.REACT_APP_BACKEND_URL;
const resolveHeroImg = (u) => (u && u.startsWith('/api/') ? `${HERO_API}${u}` : u);

const DEFAULT_HEADLINE = {
  kicker: 'PRECISION. PERFORMANCE. STYLE.',
  title: "CANADA\u2019S LARGEST SELECTION OF\nPREMIUM BODY KITS & CARBON FIBER COMPONENTS",
  subtext: 'Premium carbon fiber parts, custom fabrication and performance-driven design for those who demand more.',
};

const HeroSlideshow = () => {
  const [index, setIndex] = useState(0);
  const [slides, setSlides] = useState(HERO_SLIDES);
  const [headline, setHeadline] = useState(DEFAULT_HEADLINE);
  const total = slides.length;

  // Load admin-managed hero (falls back to the built-in slides/headline).
  useEffect(() => {
    let alive = true;
    fetch(`${HERO_API}/api/hero`)
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => {
        if (!alive || !d) return;
        if (Array.isArray(d.slides) && d.slides.length) {
          setSlides(
            d.slides.map((s) => ({
              image: resolveHeroImg(s.image),
              label: s.label || '',
              cta: { label: s.ctaLabel || 'SHOP NOW', to: s.ctaTo || '/' },
            }))
          );
        }
        if (d.headline && (d.headline.title || d.headline.kicker || d.headline.subtext)) {
          setHeadline({ ...DEFAULT_HEADLINE, ...d.headline });
        }
      })
      .catch(() => {});
    return () => { alive = false; };
  }, []);

  // auto-advance every 3.5 seconds
  useEffect(() => {
    if (total <= 1) return undefined;
    const t = setInterval(() => setIndex((i) => (i + 1) % total), 3500);
    return () => clearInterval(t);
  }, [total]);

  const go = (i) => setIndex((i + total) % total);
  const active = slides[index % total] || slides[0];

  return (
    <section className="relative min-h-screen flex items-center overflow-hidden bg-black">
      {/* Slides (cross-fade with Ken-Burns cinematic motion) */}
      {slides.map((s, i) => (
        <div
          key={i}
          className={`absolute inset-0 overflow-hidden transition-opacity duration-1000 ${
            i === index ? 'opacity-100' : 'opacity-0'
          }`}
          aria-hidden={i !== index}
        >
          <div
            className={`absolute inset-0 kenburns kenburns-${(i % 6) + 1}`}
            style={{
              backgroundImage: `url(${s.image})`,
              backgroundSize: 'cover',
              backgroundPosition: 'center',
            }}
          />
          {/* Left-to-right darken for hero copy legibility */}
          <div className="absolute inset-0 bg-gradient-to-r from-black/85 via-black/55 to-black/5 pointer-events-none" />
        </div>
      ))}
      {/* Bottom darken for readability */}
      <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-transparent to-black pointer-events-none" />

      {/* Hero copy */}
      <div className="relative z-10 max-w-[1400px] mx-auto px-6 lg:px-10 w-full pt-32 pb-32">
        <div className="max-w-2xl hero-copy-drift">
          <div className="flex items-center gap-3 mb-6">
            <span className="h-[2px] w-8 bg-[#FF6B00]" />
            <span className="font-display tracking-[0.28em] text-xs text-[#FF6B00] font-semibold" data-testid="hero-kicker">
              {headline.kicker}
            </span>
          </div>
          <h1
            className="font-display text-3xl md:text-5xl lg:text-[4rem] leading-[1.05] tracking-tight mb-6 max-w-3xl whitespace-pre-line"
            data-testid="hero-title"
          >
            {headline.title}
          </h1>
          <div className="h-[2px] w-20 bg-[#FF6B00] mb-6" />
          <p className="text-neutral-200 text-lg max-w-md mb-8 leading-relaxed" data-testid="hero-subtext">
            {headline.subtext}
          </p>
          <div className="flex flex-wrap gap-4 items-center">
            {active?.cta?.to && (
              <Link
                to={active.cta.to}
                key={active.cta.to}
                data-testid="hero-cta"
                className="bg-[#FF6B00] hover:bg-[#E25500] transition-colors px-7 py-4 font-display tracking-[0.22em] text-sm font-bold"
              >
                {active.cta.label}
              </Link>
            )}
            <Link
              to="/services"
              className="border-2 border-white/70 hover:border-white hover:bg-white/10 transition-colors px-7 py-4 font-display tracking-[0.22em] text-sm font-bold"
            >
              OUR SERVICES
            </Link>
          </div>
        </div>
      </div>

      {/* Slide controls */}
      <div className="absolute bottom-8 left-0 right-0 z-20">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10 flex items-center justify-between gap-6">
          <div className="font-display tracking-[0.22em] text-[11px] text-white/80 hidden sm:block">
            {(active?.label || '').toUpperCase()}
          </div>
          <div className="flex items-center gap-4 ml-auto">
            <div className="flex gap-2">
              {slides.map((_, i) => (
                <button
                  key={i}
                  type="button"
                  onClick={() => go(i)}
                  aria-label={`Go to slide ${i + 1}`}
                  className={`h-1 transition-all ${
                    i === index ? 'w-10 bg-[#FF6B00]' : 'w-6 bg-white/30 hover:bg-white/60'
                  }`}
                />
              ))}
            </div>
            <div className="hidden sm:flex items-center gap-2">
              <button
                type="button"
                onClick={() => go(index - 1)}
                aria-label="Previous slide"
                className="h-10 w-10 border-2 border-white/40 text-white hover:border-[#FF6B00] hover:text-[#FF6B00] flex items-center justify-center transition-colors"
              >
                <ArrowLeft size={16} />
              </button>
              <button
                type="button"
                onClick={() => go(index + 1)}
                aria-label="Next slide"
                className="h-10 w-10 border-2 border-white/40 text-white hover:border-[#FF6B00] hover:text-[#FF6B00] flex items-center justify-center transition-colors"
              >
                <ArrowRight size={16} />
              </button>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

const Home = () => {
  return (
    <div className="bg-black text-white">
      {/* HERO SLIDESHOW */}
      <HeroSlideshow />

      {/* CURATED ICONIC BRANDS */}
      <section className="bg-black py-20 lg:py-24 border-y border-white/10">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
          <div className="text-center mb-12">
            <h2 className="font-display italic text-3xl md:text-5xl tracking-wide mb-6">CURATED ICONIC BRANDS</h2>
            <div className="h-px w-40 bg-white/40 mx-auto mb-5" />
            <div className="font-display tracking-[0.28em] text-xs md:text-sm text-neutral-300">
              DRY CARBON &nbsp;&bull;&nbsp; FORGED CARBON &nbsp;&bull;&nbsp; PERFECT FITMENT
            </div>
          </div>

          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-x-8 gap-y-10 items-center justify-items-center">
            {[
              { name: 'DarwinPro', src: '/assets/shopify-migrated/Darwinpro-Logo-White-59d385fe02171276.png', to: '/brands/darwinpro-aerodynamics' },
              { name: 'Paktechz',  src: '/assets/shopify-migrated/paktechz-logo-white-d1bc28e76ba9c8e2.png', to: '/brands/paktechz' },
              { name: 'Fortune',   src: '/assets/shopify-migrated/Fortune_Logo_White-add8ef8898a01d65.png', to: '/brands/fortune' },
              { name: 'Sooqoo',    src: '/assets/shopify-migrated/Sooqoo-Logo-White-ca36b7117d81df7f.png', to: '/brands/sooqoo-carbon' },
              { name: 'Karbel',    src: '/assets/shopify-migrated/Karbel-Logo-White-b502262fdb9487e7.png', to: '/brands/karbel-carbon' },
              { name: 'Takd Carbon', src: '/assets/brand-logos/takd.png', to: '/brands/takd-carbon' },
            ].map((b) => {
              const inner = b.text ? (
                <div className="flex items-center gap-2 text-white">
                  <span className="font-display text-2xl font-bold tracking-wider" style={{ color: '#FF6B00' }}>T</span>
                  <span className="font-display text-2xl font-bold tracking-[0.18em]">TAKD</span>
                  <span className="font-display text-2xl font-bold tracking-[0.18em] text-neutral-200">CARBON</span>
                </div>
              ) : (
                <img
                  src={b.src}
                  alt={b.name}
                  className="max-h-12 w-auto object-contain opacity-90 group-hover:opacity-100 transition-opacity"
                  loading="lazy"
                />
              );
              return b.to ? (
                <Link
                  key={b.name}
                  to={b.to}
                  aria-label={`Shop ${b.name}`}
                  data-testid={`home-brand-logo-${b.name.toLowerCase().replace(/\s+/g, '-')}`}
                  className="group h-14 flex items-center justify-center w-full transition-transform duration-300 hover:scale-105"
                >
                  {inner}
                </Link>
              ) : (
                <div key={b.name} className="h-14 flex items-center justify-center w-full">
                  {inner}
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* WHAT WE DO */}
      <section className="py-24">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
          <div className="text-center mb-16">
            <div className="font-display tracking-[0.3em] text-xs text-[#FF6B00] font-semibold mb-3">WHAT WE DO</div>
            <h2 className="font-display text-4xl md:text-5xl lg:text-6xl tracking-tight mb-4">DRIVEN BY YOU. BUILT BY US.</h2>
            <p className="text-neutral-400 max-w-2xl mx-auto">From concept to creation, we bring your vision to life with precision engineering and premium materials.</p>
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-8">
            {SERVICES.map((s) => {
              const Icon = iconMap[s.icon] || Wrench;
              return (
                <div key={s.id} className="group relative text-center px-4 py-8 border border-transparent hover:border-white/10 transition-colors">
                  <div className="mx-auto mb-6 h-16 w-16 flex items-center justify-center border border-[#FF6B00]/40 group-hover:border-[#FF6B00] transition-colors">
                    <Icon className="text-[#FF6B00]" size={30} strokeWidth={1.5} />
                  </div>
                  <h3 className="font-display tracking-[0.18em] text-sm font-bold mb-3">{s.title.toUpperCase()}</h3>
                  <p className="text-neutral-400 text-sm leading-relaxed">{s.desc}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* FEATURED BUILDS — horizontal carousel, 3 at a time */}
      <FeaturedBuildsCarousel />

      {/* WHY CHOOSE US */}
      <section
        className="py-24 relative"
        style={{
          backgroundImage: `linear-gradient(90deg, rgba(0,0,0,0.95) 0%, rgba(0,0,0,0.85) 50%, rgba(0,0,0,0.75) 100%), url(${IMAGES.carbonTexture})`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
        }}
      >
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10 grid lg:grid-cols-2 gap-14">
          <div>
            <div className="font-display tracking-[0.3em] text-xs text-[#FF6B00] font-semibold mb-3">WHY CHOOSE FORTUNE AUTO DESIGN</div>
            <h2 className="font-display text-4xl md:text-5xl tracking-tight mb-6">CRAFT. PRECISION. DEDICATION.</h2>
            <p className="text-neutral-300 max-w-md mb-8 leading-relaxed">
              We are automotive enthusiasts and craftsmen dedicated to delivering the highest quality parts and service. Every detail matters, because your build deserves nothing less.
            </p>
            <Link to="/about" className="inline-block border-2 border-white/70 hover:border-white hover:bg-white/10 transition-colors px-6 py-3 font-display tracking-[0.22em] text-xs font-bold">
              LEARN MORE
            </Link>
          </div>
          <div className="grid grid-cols-2 gap-8">
            {WHY_US.map((w) => {
              const Icon = iconMap[w.icon] || ShieldCheck;
              return (
                <div key={w.title} className="text-center">
                  <div className="mx-auto mb-4 h-14 w-14 flex items-center justify-center">
                    <Icon className="text-[#FF6B00]" size={36} strokeWidth={1.5} />
                  </div>
                  <h4 className="font-display tracking-[0.18em] text-sm font-bold mb-2">{w.title}</h4>
                  <p className="text-neutral-400 text-sm leading-relaxed">{w.desc}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* RED CTA BANNER */}
      <section className="relative overflow-hidden bg-[#FF6B00] text-white">
        <div className="absolute inset-0 opacity-10 pointer-events-none" style={{
          backgroundImage: `url(${IMAGES.carbonTexture})`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          mixBlendMode: 'overlay',
        }} />
        <div className="relative max-w-[1400px] mx-auto px-6 lg:px-10 py-16 md:py-20 grid lg:grid-cols-[1fr_auto] gap-8 items-center">
          <div>
            <div className="font-display tracking-[0.3em] text-[11px] text-white/80 font-semibold mb-3">START YOUR BUILD</div>
            <h2 className="font-display text-4xl md:text-5xl lg:text-6xl tracking-tight leading-[1.05]">
              READY TO BUILD<br />
              SOMETHING UNFORGETTABLE?
            </h2>
            <p className="text-white/90 mt-5 max-w-xl leading-relaxed">
              From custom carbon fiber bodywork to bespoke forged wheels &mdash; our team is ready to bring your vision to life. Reach out for a personalized quote.
            </p>
          </div>
          <div className="flex flex-wrap gap-4">
            <Link
              to="/contact"
              className="inline-flex items-center gap-3 bg-white text-[#FF6B00] hover:bg-black hover:text-white transition-colors px-8 py-5 font-display tracking-[0.22em] text-sm font-bold"
            >
              REQUEST A QUOTE
              <ArrowRight size={18} />
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Home;
